import React, { useState, useMemo } from 'react';
import {
  User,
  Briefcase,
  Phone,
  Mail,
  MapPin,
  Award,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
  Clock,
  Plus,
  X,
  Sparkles,
  Layers,
  ArrowRight,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { SERVICE_CATEGORIES, getSubcategoriesForCategory } from '../../data/categories';
import { LocationSelector } from '../common/LocationSelector';
import { getLgasForState, isValidLgaForState } from '../../data/nigerianLocations';
import { VerificationBadge } from '../common/VerificationBadge';
import { isValidCoordinatePair, isWithinNigeriaBounds } from '../../utils/locationCoordinates';
import { ProfilePhotoUploader } from '../common/ProfilePhotoUploader';
import { updateVerifiedProfilePhoto } from '../../lib/supabaseService';

interface ProfessionalProfileEditSectionProps {
  onNavigateToVerification?: () => void;
}

export const ProfessionalProfileEditSection: React.FC<ProfessionalProfileEditSectionProps> = ({
  onNavigateToVerification,
}) => {
  const { currentUser, updateProfessionalProfile } = useApp();

  const [avatarUrl, setAvatarUrl] = useState(currentUser?.avatarUrl || '');
  const [fullName, setFullName] = useState(currentUser?.fullName || '');
  const [businessName, setBusinessName] = useState(currentUser?.businessName || '');
  const [phone, setPhone] = useState(currentUser?.phone || '');
  const [primaryCategory, setPrimaryCategory] = useState(currentUser?.primaryCategory || 'plumber');
  const [secondaryCategories, setSecondaryCategories] = useState<string[]>(
    currentUser?.secondaryCategories || []
  );
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>(
    currentUser?.specialties || []
  );
  const [skills, setSkills] = useState<string[]>(
    currentUser?.skills && currentUser.skills.length > 0
      ? currentUser.skills
      : ['pipe leak detection', 'pipe fitting', 'water pressure diagnostics']
  );
  const [newSkillInput, setNewSkillInput] = useState('');
  const [yearsExperience, setYearsExperience] = useState<number>(currentUser?.yearsExperience || 5);
  const [hourlyRate, setHourlyRate] = useState<number>(currentUser?.hourlyRate || 8000);
  const [state, setState] = useState(currentUser?.state || 'Lagos');
  const [lga, setLga] = useState(currentUser?.lga || 'Ikeja');
  const [cityArea, setCityArea] = useState(currentUser?.cityArea || 'Allen Avenue');
  const [serviceAreas, setServiceAreas] = useState<string[]>(
    currentUser?.serviceAreas && currentUser.serviceAreas.length > 0
      ? currentUser.serviceAreas
      : ['Ikeja', 'Maryland', 'Surulere']
  );
  const [serviceLatitude, setServiceLatitude] = useState<string>(
    typeof currentUser?.serviceLatitude === 'number'
      ? String(currentUser.serviceLatitude)
      : typeof currentUser?.latitude === 'number'
      ? String(currentUser.latitude)
      : ''
  );
  const [serviceLongitude, setServiceLongitude] = useState<string>(
    typeof currentUser?.serviceLongitude === 'number'
      ? String(currentUser.serviceLongitude)
      : typeof currentUser?.longitude === 'number'
      ? String(currentUser.longitude)
      : ''
  );
  const [serviceRadiusKm, setServiceRadiusKm] = useState<number>(
    currentUser?.serviceRadiusKm || 20
  );
  const [bio, setBio] = useState(currentUser?.bio || '');
  const [isAvailable, setIsAvailable] = useState<boolean>(currentUser?.isAvailable ?? true);

  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Available LGAs for current state
  const availableStateLgas = useMemo(() => {
    return getLgasForState(state);
  }, [state]);

  // Compute profile completion breakdown
  const completionStats = useMemo(() => {
    const checklist = [
      { name: 'Full Name', done: Boolean(fullName.trim()) },
      { name: 'Business Name', done: Boolean(businessName.trim()) },
      { name: 'Phone Number', done: Boolean(phone.trim()) },
      { name: 'Primary Trade', done: Boolean(primaryCategory) },
      { name: 'Experience & Rate', done: yearsExperience > 0 && hourlyRate > 0 },
      { name: 'Operating State & LGA', done: Boolean(state && lga) },
      { name: 'Service Coverage Areas', done: serviceAreas.length > 0 },
      { name: 'Skills & Specialties', done: skills.length > 0 },
      { name: 'Professional Bio', done: bio.length >= 20 },
    ];
    const doneCount = checklist.filter((i) => i.done).length;
    const percentage = Math.round((doneCount / checklist.length) * 100);
    return { checklist, doneCount, percentage };
  }, [fullName, businessName, phone, primaryCategory, yearsExperience, hourlyRate, state, lga, serviceAreas, skills, bio]);

  // Handle skill addition
  const handleAddSkill = (e: React.KeyboardEvent | React.MouseEvent) => {
    if ('key' in e && e.key !== 'Enter') return;
    e.preventDefault();
    const trimmed = newSkillInput.trim();
    if (trimmed && !skills.includes(trimmed)) {
      setSkills([...skills, trimmed]);
      setNewSkillInput('');
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    setSkills(skills.filter((s) => s !== skillToRemove));
  };

  // Handle Service Area toggle
  const handleToggleServiceArea = (areaLga: string) => {
    if (serviceAreas.includes(areaLga)) {
      setServiceAreas(serviceAreas.filter((a) => a !== areaLga));
    } else {
      setServiceAreas([...serviceAreas, areaLga]);
    }
  };

  // Handle Secondary Category toggle
  const handleToggleSecondaryCategory = (slug: string) => {
    if (secondaryCategories.includes(slug)) {
      setSecondaryCategories(secondaryCategories.filter((c) => c !== slug));
    } else {
      if (secondaryCategories.length >= 3) return; // max 3
      setSecondaryCategories([...secondaryCategories, slug]);
    }
  };

  // Handle Subcategory toggle
  const handleToggleSubcategory = (subSlug: string) => {
    if (selectedSubcategories.includes(subSlug)) {
      setSelectedSubcategories(selectedSubcategories.filter((s) => s !== subSlug));
    } else {
      setSelectedSubcategories([...selectedSubcategories, subSlug]);
    }
  };

  // Available subcategories for primary + secondary
  const relevantSubcategories = useMemo(() => {
    const list = [...getSubcategoriesForCategory(primaryCategory)];
    for (const sec of secondaryCategories) {
      list.push(...getSubcategoriesForCategory(sec));
    }
    return list;
  }, [primaryCategory, secondaryCategories]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!fullName.trim()) {
      setErrorMessage('Full name is required.');
      return;
    }
    if (!businessName.trim()) {
      setErrorMessage('Business / technical brand name is required.');
      return;
    }
    if (!isValidLgaForState(state, lga)) {
      setErrorMessage(`The LGA '${lga}' does not belong to '${state}'. Please select a valid LGA.`);
      return;
    }

    let parsedLat: number | undefined;
    let parsedLng: number | undefined;

    if (serviceLatitude.trim() !== '' || serviceLongitude.trim() !== '') {
      const latNum = Number(serviceLatitude);
      const lngNum = Number(serviceLongitude);
      if (!isValidCoordinatePair(latNum, lngNum) || !isWithinNigeriaBounds(latNum, lngNum)) {
        setErrorMessage(
          'Service base coordinates must be a valid latitude (4.0 to 14.0) and longitude (2.5 to 15.0) within Nigeria.'
        );
        return;
      }
      parsedLat = Number(latNum.toFixed(6));
      parsedLng = Number(lngNum.toFixed(6));
    }

    setIsSaving(true);
    try {
      await updateProfessionalProfile({
        fullName: fullName.trim(),
        avatarUrl: avatarUrl ? avatarUrl.trim() : undefined,
        businessName: businessName.trim(),
        phone: phone.trim(),
        primaryCategory,
        secondaryCategories,
        specialties: selectedSubcategories,
        skills,
        yearsExperience: Number(yearsExperience),
        hourlyRate: Number(hourlyRate),
        state: state.trim(),
        lga: lga.trim(),
        cityArea: cityArea.trim(),
        serviceAreas,
        ...(parsedLat !== undefined && parsedLng !== undefined
          ? {
              serviceLatitude: parsedLat,
              serviceLongitude: parsedLng,
              latitude: parsedLat,
              longitude: parsedLng,
              serviceLocationSource: 'exact' as const,
            }
          : {}),
        serviceRadiusKm: Number(serviceRadiusKm) || 20,
        bio: bio.trim(),
        isAvailable,
      });

      if (currentUser?.id && avatarUrl) {
        try {
          await updateVerifiedProfilePhoto(currentUser.id, avatarUrl);
        } catch {
          // Non-blocking
        }
      }

      if (currentUser?.id) {
        try {
          await fetch(`/api/professionals/${encodeURIComponent(currentUser.id)}/service-areas`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Id': currentUser.id,
              'X-User-Role': currentUser.role || 'professional',
            },
            body: JSON.stringify({
              state: state.trim(),
              lga: lga.trim(),
              cityArea: cityArea.trim(),
              serviceAreas,
              ...(parsedLat !== undefined && parsedLng !== undefined
                ? {
                    serviceLatitude: parsedLat,
                    serviceLongitude: parsedLng,
                  }
                : {}),
              serviceRadiusKm: Number(serviceRadiusKm) || 20,
            }),
          });
        } catch {
          // AppContext update already succeeded
        }
      }

      setSuccessMessage('Professional profile, services, and coverage areas updated successfully.');
    } catch (err: any) {
      setErrorMessage(err?.message || 'Failed to update professional profile.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in text-left">
      {/* Top Banner: Profile Completion vs. Verification Status Separation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Card 1: Profile Completion */}
        <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <Briefcase className="w-4 h-4 text-emerald-600" />
              <span>Profile Completion</span>
            </span>
            <span className="text-sm font-extrabold text-emerald-700">
              {completionStats.percentage}% Complete
            </span>
          </div>

          <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
            <div
              className="bg-emerald-600 h-full transition-all duration-300 rounded-full"
              style={{ width: `${completionStats.percentage}%` }}
            />
          </div>

          <p className="text-xs text-slate-500">
            {completionStats.percentage === 100
              ? '✓ Your professional information and service areas are 100% complete.'
              : `${completionStats.doneCount} of 9 profile requirements filled. Complete your profile for better customer quote matching.`}
          </p>
        </div>

        {/* Card 2: Identity Verification Status */}
        <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs flex flex-col justify-between space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span>Identity Verification</span>
            </span>
            <VerificationBadge status={currentUser?.verificationStatus} size="sm" />
          </div>

          <div className="text-xs text-slate-600 space-y-1">
            <p>
              Status:{' '}
              <strong className="text-slate-900 uppercase">
                {currentUser?.verificationStatus || 'not_started'}
              </strong>
            </p>
            <p className="text-[11px] text-slate-400">
              Identity verification requires government ID and proof of address audited by JobMatch NG compliance admins.
            </p>
          </div>

          {onNavigateToVerification && currentUser?.verificationStatus !== 'verified' && (
            <button
              type="button"
              onClick={onNavigateToVerification}
              className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1 cursor-pointer pt-1"
            >
              <span>Manage Document Verification</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {successMessage && (
        <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <span className="font-semibold">{successMessage}</span>
        </div>
      )}

      {errorMessage && (
        <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
          <span className="font-semibold">{errorMessage}</span>
        </div>
      )}

      {/* Main Profile Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-xs space-y-6">
        <div className="border-b border-slate-100 pb-4">
          <h3 className="text-base font-extrabold text-slate-900">Professional & Business Information</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Keep your business trade identity, rates, and coverage areas accurate for incoming customer job notifications.
          </p>
        </div>

        {/* Work Availability Switch Card */}
        <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <div className="flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full ${isAvailable ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`}></span>
              <span className="text-xs font-black text-slate-900">
                {isAvailable ? 'Currently Available for New Jobs' : 'Currently Off-Duty / Busy'}
              </span>
            </div>
            <p className="text-[11px] text-slate-500">
              {isAvailable
                ? 'Your profile is active in search results and you will receive instant notifications for new job matches in your trade.'
                : 'Your profile is temporarily hidden from customer search results until you set your availability back to active.'}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setIsAvailable(!isAvailable)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden ${
              isAvailable ? 'bg-emerald-600' : 'bg-slate-300'
            }`}
            aria-label="Set Availability Toggle"
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                isAvailable ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Profile Photo */}
        <div className="bg-slate-50/80 p-4 sm:p-5 rounded-2xl border border-slate-200/80">
          <ProfilePhotoUploader
            photoUrl={avatarUrl}
            onChange={setAvatarUrl}
            userRole="professional"
            label="Professional Profile Photograph"
          />
        </div>

        {/* Section 1: Basic Identity */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Full Legal Name
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:outline-hidden focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Business / Trade Brand Name
            </label>
            <input
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="e.g. Apex Precision Plumbing Services"
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:outline-hidden focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Nigerian Mobile Phone
            </label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 0803 123 4567"
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:outline-hidden focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Registered Email (Account Login)
            </label>
            <input
              type="email"
              value={currentUser?.email || ''}
              disabled
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-100 text-slate-500 text-xs font-medium cursor-not-allowed"
            />
          </div>
        </div>

        {/* Section 2: Trade Categories & Experience */}
        <div className="pt-4 border-t border-slate-100 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Primary Trade Category
              </label>
              <select
                value={primaryCategory}
                onChange={(e) => setPrimaryCategory(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-semibold text-slate-900 focus:outline-hidden focus:border-emerald-600 bg-white"
              >
                {SERVICE_CATEGORIES.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Years of Field Experience
              </label>
              <input
                type="number"
                min={1}
                max={50}
                value={yearsExperience}
                onChange={(e) => setYearsExperience(Number(e.target.value))}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-semibold text-slate-900 focus:outline-hidden focus:border-emerald-600"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Hourly Diagnostic Rate (₦)
              </label>
              <input
                type="number"
                min={1000}
                step={500}
                value={hourlyRate}
                onChange={(e) => setHourlyRate(Number(e.target.value))}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-semibold text-slate-900 focus:outline-hidden focus:border-emerald-600"
                required
              />
            </div>
          </div>

          {/* Secondary Categories */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Additional Trade Categories (Optional, Max 3)
            </label>
            <div className="flex flex-wrap gap-2">
              {SERVICE_CATEGORIES.filter((c) => c.slug !== primaryCategory).map((cat) => {
                const isSelected = secondaryCategories.includes(cat.slug);
                return (
                  <button
                    key={cat.slug}
                    type="button"
                    onClick={() => handleToggleSecondaryCategory(cat.slug)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer border ${
                      isSelected
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                        : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {isSelected ? '✓ ' : '+ '}
                    {cat.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Subcategories under selected trades */}
          {relevantSubcategories.length > 0 && (
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Specialized Trade Subcategories ({selectedSubcategories.length} selected)
              </label>
              <p className="text-[11px] text-slate-400 mb-2">
                Select the specific specialized services you offer under your trades:
              </p>
              <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-2 bg-slate-50 rounded-2xl border border-slate-200">
                {relevantSubcategories.map((sub) => {
                  const isSelected = selectedSubcategories.includes(sub.slug);
                  return (
                    <button
                      key={`${sub.categorySlug}-${sub.slug}`}
                      type="button"
                      onClick={() => handleToggleSubcategory(sub.slug)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                        isSelected
                          ? 'bg-emerald-600 text-white font-bold shadow-2xs'
                          : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {isSelected ? '✓ ' : '+ '}
                      {sub.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Skills & Specialties */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Skills & Specialties
            </label>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="text"
                value={newSkillInput}
                onChange={(e) => setNewSkillInput(e.target.value)}
                onKeyDown={handleAddSkill}
                placeholder="e.g. Submersible pump repair, leak detection"
                className="flex-1 px-3.5 py-2 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:outline-hidden focus:border-emerald-600"
              />
              <button
                type="button"
                onClick={handleAddSkill}
                className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold cursor-pointer"
              >
                Add Skill
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {skills.map((skill) => (
                <span
                  key={skill}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-semibold"
                >
                  <span>{skill}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveSkill(skill)}
                    className="hover:text-rose-600 cursor-pointer ml-1"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Section 3: Location & Service Coverage Areas */}
        <div className="pt-4 border-t border-slate-100 space-y-4">
          <div className="space-y-1">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
              Operating State & Base LGA
            </h4>
            <p className="text-[11px] text-slate-500">
              State selection restricts and determines available LGAs across all 36 Nigerian States + FCT.
            </p>
          </div>

          <LocationSelector
            idPrefix="pro-profile-loc"
            selectedState={state}
            selectedLga={lga}
            selectedCityArea={cityArea}
            onStateChange={(newState) => {
              setState(newState);
              // reset service areas to new state LGAs
              const newLgas = getLgasForState(newState);
              setServiceAreas(newLgas.slice(0, 3));
            }}
            onLgaChange={(newLga) => setLga(newLga)}
            onCityAreaChange={(newArea) => setCityArea(newArea)}
            theme="light"
            layout="stacked"
            showCountry={false}
            showCityArea={true}
          />

          {/* Service Areas Multi-Selection */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Service Areas Covered in {state} ({serviceAreas.length} selected)
            </label>
            <p className="text-[11px] text-slate-400 mb-2">
              Select LGAs within {state} where you are available to take on client jobs:
            </p>

            <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto p-2 bg-slate-50 rounded-2xl border border-slate-200">
              {availableStateLgas.map((areaLga) => {
                const isSelected = serviceAreas.includes(areaLga);
                return (
                  <button
                    key={areaLga}
                    type="button"
                    onClick={() => handleToggleServiceArea(areaLga)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-emerald-600 text-white font-bold shadow-2xs'
                        : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {isSelected ? '✓ ' : '+ '}
                    {areaLga}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Validated Service Base Coordinates & Coverage Radius */}
          <div className="p-4 rounded-2xl bg-emerald-50/50 border border-emerald-200/80 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h5 className="text-xs font-extrabold text-slate-900 flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                  <span>Nearby Map Discovery Coordinates & Travel Radius</span>
                </h5>
                <p className="text-[11px] text-slate-500">
                  Set your workshop or operating base coordinates so nearby customers can discover you within 5 km – 50 km. Your private home address is never shown publicly.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (typeof navigator !== 'undefined' && navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                      (pos) => {
                        setServiceLatitude(String(Number(pos.coords.latitude.toFixed(5))));
                        setServiceLongitude(String(Number(pos.coords.longitude.toFixed(5))));
                      },
                      () => {
                        setErrorMessage('Could not read browser GPS. You can enter coordinates or use your LGA base.');
                      },
                      { enableHighAccuracy: true, timeout: 10000 }
                    );
                  }
                }}
                className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shrink-0 cursor-pointer"
              >
                Use Current Base GPS
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">
                  Service Base Latitude (°N)
                </label>
                <input
                  type="number"
                  step="0.00001"
                  min={4.0}
                  max={14.2}
                  value={serviceLatitude}
                  onChange={(e) => setServiceLatitude(e.target.value)}
                  placeholder="e.g. 6.6018"
                  className="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-xs font-semibold text-slate-900"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">
                  Service Base Longitude (°E)
                </label>
                <input
                  type="number"
                  step="0.00001"
                  min={2.5}
                  max={15.2}
                  value={serviceLongitude}
                  onChange={(e) => setServiceLongitude(e.target.value)}
                  placeholder="e.g. 3.3515"
                  className="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-xs font-semibold text-slate-900"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">
                  Max Travel Radius (km)
                </label>
                <select
                  value={serviceRadiusKm}
                  onChange={(e) => setServiceRadiusKm(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-xs font-semibold text-slate-900"
                >
                  <option value={5}>5 km radius</option>
                  <option value={10}>10 km radius</option>
                  <option value={20}>20 km radius</option>
                  <option value={50}>50 km radius</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Professional Description & Bio */}
        <div className="pt-4 border-t border-slate-100 space-y-3">
          <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
            Professional Bio & Experience Description
          </label>
          <textarea
            rows={4}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Tell clients about your trade background, apprenticeships, warranty terms, and workmanship standards..."
            className="w-full p-3.5 rounded-xl border border-slate-300 text-xs font-medium text-slate-900 focus:outline-hidden focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20 leading-relaxed"
          />
        </div>

        {/* Section 5: Availability */}
        <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
          <div>
            <h4 className="text-xs font-bold text-slate-900">Job Availability Status</h4>
            <p className="text-[11px] text-slate-500">
              When toggled on, your profile is open for new customer quotes in your coverage areas.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setIsAvailable(!isAvailable)}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer border ${
              isAvailable
                ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                : 'bg-slate-100 text-slate-600 border-slate-200'
            }`}
          >
            {isAvailable ? '● Available for Work' : '○ Currently Busy / Offline'}
          </button>
        </div>

        {/* Form Action */}
        <div className="pt-6 border-t border-slate-100 flex items-center justify-end gap-3">
          <button
            type="submit"
            disabled={isSaving}
            className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {isSaving ? (
              <span>Saving Profile...</span>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                <span>Save Profile & Service Areas</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
